import { useCallback } from 'react'

import { useSanitisedDispatch } from '@/legacy-src/redux/redux.utils'

import { useSession } from '../contexts/session-context'
import UserActions, { USER_TYPE } from '../redux/UserRedux'
import { type CompanyEmployee, type UserType } from '../types/UserStateType'

type ExtendedUserType = {
  readonly user: UserType | null
  readonly isClient: boolean
  readonly isTherapist: boolean
  readonly isLoadingUser: boolean
  readonly isCompanyAdmin: boolean
  readonly isCompanyEmployee: boolean
  readonly companyAdminId: number | null
  readonly company: CompanyEmployee | null
  readonly companyId: number | null
  readonly allCompanyIds: number[]
  readonly refreshUser: () => void
  readonly callIdentity: string
}

const useUser = (): ExtendedUserType => {
  const { user, isAuthenticating } = useSession()
  const dispatch = useSanitisedDispatch()
  const refreshUser = useCallback(() => {
    dispatch(UserActions.getUserDataRequest(null, true, { event: 'loginSuccessful' }))
  }, [])

  const isTherapist = user?.type === USER_TYPE.THERAPIST
  const isClient = user?.type === USER_TYPE.CLIENT

  const isCompanyEmployee = (user?.companiesEmployee && user?.companiesEmployee?.length > 0) || false
  const allCompanyIds = user?.companiesEmployee?.map((ce) => ce.id).filter(Boolean) || []
  const company = user?.companiesEmployee?.[0] || null
  const companyId = user?.companiesEmployee?.[0]?.id || null

  const isCompanyAdmin =
    user?.type === USER_TYPE.CLIENT && Array.isArray(user?.companyIds) && user?.companyIds.length > 0
  const companyAdminId = user?.companyIds?.[0] || null

  return {
    user: user || null,
    isClient,
    isTherapist,
    isLoadingUser: isAuthenticating,
    isCompanyAdmin,
    isCompanyEmployee,
    company,
    companyId,
    allCompanyIds,
    companyAdminId,
    // this is generated same way on BE to limit sending sensitive data to call providers
    callIdentity: `${isTherapist ? 't' : 'c'}-${user?.id}`,
    refreshUser,
  }
}

export default useUser
